import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, BACKEND, errMsg } from "@/lib/api";
import { PageHead } from "./Layout";

export default function Appearance() {
  const [org, setOrg] = useState<any>(null);
  const [w, setW] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [frameKey, setFrameKey] = useState(0);
  useEffect(() => {
    Promise.all([api.get("/orgs/me"), api.get("/orgs/me/widget")]).then(([o, x]) => {
      setOrg({ primary_color: o.data.branding.primary_color, secondary_color: o.data.branding.secondary_color, logo_url: o.data.logo_url });
      setW(x.data);
    });
  }, []);
  if (!org || !w) return <p className="text-muted-foreground">Loading…</p>;
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/orgs/me", { primary_color: org.primary_color, secondary_color: org.secondary_color });
      await api.patch("/orgs/me/widget", { headline: w.headline, cta_text: w.cta_text, footer_note: w.footer_note, privacy_url: w.privacy_url || "", terms_url: w.terms_url || "", require_phone: !!w.require_phone });
      toast.success("Appearance saved");
      setFrameKey((k) => k + 1);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const upload = async (file?: File) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/orgs/me/logo", fd);
      setOrg({ ...org, logo_url: data.logo_url });
      setFrameKey((k) => k + 1);
      toast.success("Logo uploaded");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const removeLogo = async () => { await api.delete("/orgs/me/logo"); setOrg({ ...org, logo_url: null }); setFrameKey((k) => k + 1); };
  return (
    <div className="fade-up">
      <PageHead title="Widget Appearance" sub="Changes apply to both the embedded widget and your hosted quote page." />
      <div className="grid lg:grid-cols-[1fr_400px] gap-8">
        <div className="bg-card border rounded-xl p-6 space-y-6">
          <div>
            <Label className="text-xs">Logo (PNG, JPEG or WebP, max 2 MB)</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="h-16 w-32 border rounded-lg grid place-items-center bg-secondary/50 overflow-hidden">
                {org.logo_url ? <img src={`${BACKEND}${org.logo_url}`} alt="Logo" className="max-h-14 max-w-[120px] object-contain" data-testid="logo-preview" /> : <span className="text-xs text-muted-foreground">No logo</span>}
              </div>
              <label className="inline-flex items-center gap-2 h-10 px-4 rounded-md border cursor-pointer text-sm hover:bg-secondary" data-testid="logo-upload-label">
                <Upload className="h-4 w-4" /> Upload
                <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(e) => upload(e.target.files?.[0])} data-testid="logo-upload-input" />
              </label>
              {org.logo_url && <Button variant="ghost" size="sm" onClick={removeLogo} data-testid="logo-remove-button"><X className="h-4 w-4" /></Button>}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {(["primary_color", "secondary_color"] as const).map((k) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs">{k === "primary_color" ? "Primary (buttons, accents)" : "Secondary (headings)"}</Label>
                <div className="flex gap-2">
                  <input type="color" value={org[k]} onChange={(e) => setOrg({ ...org, [k]: e.target.value.toUpperCase() })} className="h-10 w-12 rounded border cursor-pointer" data-testid={`${k}-picker`} />
                  <Input value={org[k]} onChange={(e) => setOrg({ ...org, [k]: e.target.value })} className="font-mono" data-testid={`${k}-input`} />
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Headline</Label><Input value={w.headline} onChange={(e) => setW({ ...w, headline: e.target.value })} data-testid="widget-headline-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Button text (for your website link)</Label><Input value={w.cta_text} onChange={(e) => setW({ ...w, cta_text: e.target.value })} data-testid="widget-cta-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Footer note (shown under the estimate disclaimer)</Label><Textarea rows={3} value={w.footer_note} onChange={(e) => setW({ ...w, footer_note: e.target.value })} data-testid="widget-footer-input" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">Your privacy policy URL</Label><Input placeholder="https://" value={w.privacy_url || ""} onChange={(e) => setW({ ...w, privacy_url: e.target.value })} data-testid="widget-privacy-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Your terms URL</Label><Input placeholder="https://" value={w.terms_url || ""} onChange={(e) => setW({ ...w, terms_url: e.target.value })} data-testid="widget-terms-input" /></div>
          </div>
          <label className="flex items-center justify-between gap-4 border rounded-lg p-3">
            <span className="text-sm"><span className="font-medium">Require phone number</span><br /><span className="text-xs text-muted-foreground">Name and email are always required before measuring.</span></span>
            <Switch checked={!!w.require_phone} onCheckedChange={(v) => setW({ ...w, require_phone: v })} data-testid="require-phone-switch" />
          </label>
          <Button onClick={save} disabled={busy} data-testid="appearance-save-button">{busy ? "Saving…" : "Save appearance"}</Button>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Live preview (production renderer)</p>
          <iframe key={frameKey} title="Widget preview" src={`/embed/${w.public_id}?preview=1`} className="w-full h-[680px] rounded-xl border bg-white" data-testid="appearance-preview-iframe" />
        </div>
      </div>
    </div>
  );
}
